import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TbShoppingCart } from "react-icons/tb";
import { FiMenu } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { State } from "../../app/store";
import { FINALIZE_PAGE } from "../../app/routes";
import EAIconButton from "../../app/components/shared/Buttons/EAIconButton";
import useDesktopSize from "../../utils/hooks/useDesktopSize";
import LanguageSelect from "../LanguageSelect";
import LoginNavButton from "../NavBar/shared/LoginNavButton";
import BasketNavButton from "../NavBar/shared/BasketNavButton";
import "./embedNavigation.scss";

const MobileEmbedToolbar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { allowToMakeAccount, languageSelectActive } = useSelector((state: State) => state.config.configuration);
  const basketLength = useSelector((state: State) => state.basket.basketLength);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const hasMenu = allowToMakeAccount || languageSelectActive;

  useEffect(() => {
    if (!isMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!toolbarRef.current?.contains(event.target as Node)) setIsMenuOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMenuOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  const handleCloseMenu = () => setIsMenuOpen(false);

  return (
    <div className="embed-toolbar embed-toolbar--mobile" ref={toolbarRef}>
      <div className="embed-toolbar__icons">
        {!isMenuOpen && (
          <span className="basket-anchor" data-basket-anchor>
            <EAIconButton
              title={t("koszyk")}
              onClick={() => navigate(FINALIZE_PAGE)}
              icon={<TbShoppingCart />}
              variant="icon"
              badgeValue={basketLength}
            />
          </span>
        )}
        {hasMenu && (
          <EAIconButton
            title={t("nawigacja")}
            onClick={() => setIsMenuOpen((isOpen) => !isOpen)}
            icon={isMenuOpen ? <IoClose /> : <FiMenu />}
            variant="icon"
          />
        )}
      </div>

      {isMenuOpen && (
        <div className="embed-toolbar__menu">
          <BasketNavButton onAdditionalAction={handleCloseMenu} />
          {allowToMakeAccount && <LoginNavButton onClick={handleCloseMenu} />}
          <LanguageSelect isMobile />
        </div>
      )}
    </div>
  );
};

const EmbedToolbar: React.FC = () => {
  const isDesktop = useDesktopSize();
  const allowToMakeAccount = useSelector((state: State) => state.config.configuration.allowToMakeAccount);

  if (!isDesktop) {
    return <MobileEmbedToolbar />;
  }

  return (
    <div className="embed-toolbar">
      <LanguageSelect />
      {allowToMakeAccount && <LoginNavButton />}
      <BasketNavButton fullwidth={false} />
    </div>
  );
};

export default EmbedToolbar;
